import { hideLoadingSpinner, showLoadingSpinner } from 'admin/actions/loading-spinner';
import {
  createMemberOrganization,
  createOrganizationPrivateMoneyFee,
  getOrganizationApplicationSettingOrNull,
} from 'admin/actions/organization';
import { Button } from 'admin/components/atoms/Buttons';
import { convertFlicoStringFormat } from 'admin/utils';
import _ from 'lodash';
import React, { Component } from 'react';
import CSSModules from 'react-css-modules';
import { connect } from 'react-redux';
import { Alert } from 'reactstrap';
import { bindActionCreators } from 'redux';
import styled, { css } from 'styled-components';
import { toJapaneseBankingSystemHalfWidth } from '~/javascripts/admin/utils/characters/toHalfWidth';
import toKatakana from '~/javascripts/admin/utils/characters/toKatakana';
import Grid from '../../atoms/Grid';
import Input from '../../atoms/Input';
import Label from '../../atoms/Label';
import LocalizedMask from '../../atoms/LocalizedMask';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import TextArea from '../../atoms/TextArea';
import BreadCrumb from '../../molecules/BreadCrumb';
import styles from './styles.scss';
import { testEmailAllowedLetters, validateEmail } from '~/javascripts/admin/utils/email';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '決済加盟店管理', href: '', isCurrentPage: true },
  { value: '決済加盟店登録', href: '', isCurrentPage: true },
];

const FormGrid = styled(Grid.Container)`
  margin: 16px 0;

  ${Label} {
    margin-bottom: 0;
  }

  ${Input}, ${TextArea}, ${Grid.Divider} {
    margin-bottom: 1rem;
  }
`;

const InputWithErrorWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing['16']};
    display: flex;
    flex-direction: column;

    ${Input}, ${TextArea} {
      margin-bottom: ${spacing['0']};
    }
  `}
`;

const RadioGroupWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 1rem;
`;

const RadioWrapper = styled.div`
  display: flex;
  gap: 4px;
`;

const OrgMoneyFeeWrapper = styled.div`
  border: 1px solid #e8e8e8;
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 0.5rem;
  margin-bottom: 0.5rem;
`;

const OrgMoneyFeeInputRowWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const OrgMoneyFeeInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${Input} {
    margin-bottom: 0;
  }
`;

const ErrorText = styled.span`
  ${({ theme: { colors } }) => css`
    color: ${colors.red['400']};
  `}
`;

class CreateMemberOrganization extends Component {
  constructor(props) {
    super(props);
    this.state = {
      req: {
        code: '',
        business_type: 'individual',
        corporate_number: '',
        name: '',
        private_money_ids: [],
        issuer_admin_user_email: '',
        member_admin_user_email: '',
        bank_name: '',
        bank_code: '',
        bank_branch_name: '',
        bank_branch_code: '',
        bank_account_type: 'saving',
        bank_account: '',
        bank_account_holder_name: '',
        contact_name: '',
      },
      res: {},
      codePrefix: null,
      moneyOptions: [],
      selectedMoneyOptions: [],
      moneyFees: {},
      validationErrors: {},
    };
  }

  componentDidMount() {
    this._mounted = true;
    const moneyOptions = this.toMoneyOptions(this.props.user.issuedThirdPartyMoneys());
    this.setState({ moneyOptions });

    getOrganizationApplicationSettingOrNull()
      .then((setting) => {
        if (!this._mounted) return;
        this.setState({ codePrefix: setting?.member_organization_code_prefix ?? null });
      })
      .catch(() => undefined);
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  onSelectMoney = (options) => {
    this.setReq(
      'private_money_ids',
      options.map((op) => op.value.id)
    );

    // Initialize fee data for newly selected moneys
    const newMoneyFees = { ...this.state.moneyFees };
    options.forEach((option) => {
      if (!newMoneyFees[option.value.id]) {
        newMoneyFees[option.value.id] = {
          payment_fee_rate_percentage: '0',
          system_fee: '0',
          fixed_fee: '0',
        };
      }
    });

    // Remove fee data for deselected moneys
    const selectedIds = options.map((op) => op.value.id);
    Object.keys(newMoneyFees).forEach((id) => {
      if (!selectedIds.includes(id)) {
        delete newMoneyFees[id];
      }
    });

    this.setState({ selectedMoneyOptions: options, moneyFees: newMoneyFees });
  };

  setMoneyFee = (moneyId, field, value) => {
    if (field === 'payment_fee_rate_percentage') {
      if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
        value = value.replace(/^0+/, '');
      }

      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue > 100) {
        return;
      }
    }

    this.setState((prevState) => ({
      moneyFees: {
        ...prevState.moneyFees,
        [moneyId]: {
          ...prevState.moneyFees[moneyId],
          [field]: value,
        },
      },
    }));
  };

  validateForm() {
    const errors = {};

    if (this.state.req.private_money_ids.length === 0) {
      errors.private_money_ids = '加盟するマネーを選択してください';
    }

    const code = this.state.req.code.trim();
    if (code) {
      if (!Number.isNaN(Number(code))) {
        errors.code = '記号や半角英数を組み合わせて入力してください';
      }
    } else if (!this.state.codePrefix) {
      errors.code = '組織コードを入力してください（コードプレフィックスが未設定のため自動採番できません）';
    }

    if (!this.state.req.business_type) {
      errors.business_type = '事業体の種類を選択してください';
    }

    if (this.state.req.business_type === 'corporate') {
      if (!this.state.req.corporate_number.trim()) {
        errors.corporate_number = '法人番号を入力してください';
      } else if (!/^\d{13}$/.test(this.state.req.corporate_number.trim())) {
        errors.corporate_number = '法人番号は13桁の半角数字で入力してください';
      }
    }

    if (!this.state.req.name.trim()) {
      errors.name = '組織名を入力してください';
    }

    if (!this.state.req.issuer_admin_user_email.trim()) {
      errors.issuer_admin_user_email = 'メールアドレスを入力してください';
    }

    const isIssuerEmailValid = validateEmail(this.state.req.issuer_admin_user_email);
    if (typeof isIssuerEmailValid === 'string') {
      errors.issuer_admin_user_email = isIssuerEmailValid;
    }

    if (!this.state.req.member_admin_user_email.trim()) {
      errors.member_admin_user_email = 'メールアドレスを入力してください';
    }

    const isMemberEmailValid = validateEmail(this.state.req.member_admin_user_email);
    if (typeof isMemberEmailValid === 'string') {
      errors.member_admin_user_email = isMemberEmailValid;
    }

    return errors;
  }

  onClick(e) {
    e.preventDefault();

    const errors = this.validateForm();

    if (Object.keys(errors).length > 0) {
      this.setState({ validationErrors: errors });
      return;
    }

    this.setState({ validationErrors: {} });

    this.setReq(
      'bank_account_holder_name',
      convertFlicoStringFormat(this.state.req.bank_account_holder_name)
    );
    this.onConfirm();
  }

  onConfirm() {
    if (this.state.req.bank_account === '') {
      this.setReq('bank_account_type', '');
    }

    this.props.showLoadingSpinner();

    createMemberOrganization(this.state.req)
      .then((json) => {
        this.setState({ res: json });

        // Use the code from the response — handles both manual and auto-generated codes
        const orgCode = json.code;
        if (orgCode != null) {
          /** Also always send 0 to topup_fee_rate_percentage and point_payment_fee_rate_percentage
            since the UI doesn't need/use it but they are required by the endpoint
            ref: https://coilinc.slack.com/archives/C8RSWHFHQ/p1760675528763389?thread_ts=1760083678.427209&cid=C8RSWHFHQ */
          const feePromises = this.state.selectedMoneyOptions.map((money) => {
            const feeData = this.state.moneyFees[money.value.id];
            const paymentFee = parseFloat(feeData.payment_fee_rate_percentage);
            return createOrganizationPrivateMoneyFee(orgCode, money.value.id, {
              payment_fee_rate_percentage: paymentFee || 0,
              topup_fee_rate_percentage: 0,
              point_payment_fee_rate_percentage: paymentFee || 0,
              system_fee: parseFloat(feeData.system_fee) || 0,
              fixed_fee: parseFloat(feeData.fixed_fee) || 0,
            });
          });

          return Promise.all(feePromises).then(() => {
            this.props.hideLoadingSpinner();
            const money = this.state.selectedMoneyOptions[0];
            this.props.history.push(`/settings/private-money/${money.value.id}/organizations`);
          });
        } else {
          this.props.hideLoadingSpinner();
        }
      })
      .catch((error) => {
        this.props.hideLoadingSpinner();
        console.error('Error creating organization or fees:', error);
      });
  }

  setReq(key, value) {
    let req = this.state.req;
    req[key] = value;
    this.setState({ req: req });
  }

  isInvalidParameter(param) {
    return (
      !_.isEmpty(this.state.res) &&
      this.state.res.type === 'invalid_parameters' &&
      this.state.res.errors.invalid &&
      this.state.res.errors.invalid.indexOf(param) !== -1
    );
  }

  isSendEmailError() {
    return !_.isEmpty(this.state.res) && this.state.res.type === 'temporarily_unavailable';
  }

  isOrganizationConflict() {
    return !_.isEmpty(this.state.res) && this.state.res.type === 'organization_conflict';
  }

  isOtherError() {
    return (
      !_.isEmpty(this.state.res) &&
      this.state.res.code == null &&
      this.state.res.type !== 'invalid_parameters' &&
      this.state.res.type !== 'temporarily_unavailable' &&
      this.state.res.type !== 'organization_conflict'
    );
  }

  isSuccess() {
    return !_.isEmpty(this.state.res) && this.state.res.code != null;
  }

  toMoneyOptions = (moneys) => moneys.map((m) => ({ label: m.name, value: m }));

  render() {
    return (
      <div className={this.props.className}>
        <BreadCrumb crumbs={CRUMBS} />
        <div styleName="headline">決済加盟店登録</div>
        <form onSubmit={this.onClick.bind(this)}>
          <FormGrid>
            {/* 基本情報 section */}
            <Grid.Item size={2}>基本情報</Grid.Item>

            <Grid.Item size={4} flexDirection="column">
              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>事業体の種類 <Required /></Label>
                  <RadioGroupWrapper>
                    <RadioWrapper>
                      <Radio
                        id="individual"
                        name="business-type"
                        type="radio"
                        value="individual"
                        checked={this.state.req.business_type === 'individual'}
                        onChange={(e) => this.setReq('business_type', e.target.value)}
                      />
                      <Label htmlFor="individual">個人事業主</Label>
                    </RadioWrapper>
                    <RadioWrapper>
                      <Radio
                        id="corporate"
                        name="business-type"
                        type="radio"
                        value="corporate"
                        checked={this.state.req.business_type === 'corporate'}
                        onChange={(e) => this.setReq('business_type', e.target.value)}
                      />
                      <Label htmlFor="corporate">法人</Label>
                    </RadioWrapper>
                  </RadioGroupWrapper>
                  {this.state.validationErrors.business_type && (
                    <ErrorText>{this.state.validationErrors.business_type}</ErrorText>
                  )}
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>
                    加盟するマネー <Required />
                  </Label>
                  <Select
                    options={this.state.moneyOptions}
                    value={this.state.selectedMoneyOptions}
                    onChange={this.onSelectMoney}
                    isMulti="true"
                    placeholder="加盟するマネーを選択して下さい"
                  />
                  {this.state.validationErrors.private_money_ids && (
                    <ErrorText>{this.state.validationErrors.private_money_ids}</ErrorText>
                  )}
                </InputWithErrorWrapper>
              </Grid.Item>

              {this.state.req.business_type === 'corporate' && (
                <Grid.Item flexDirection="column">
                  <InputWithErrorWrapper>
                    <Label>法人番号 <Required /></Label>
                    <Input
                      maxLength="13"
                      pattern="\d{13}"
                      title="法人番号は13桁の半角数字である必要があります"
                      onChange={(e) => {
                        if (/^\d*$/.test(e.target.value)) {
                          this.setReq('corporate_number', e.target.value);
                        }
                      }}
                      fullWidth
                    />
                    {this.state.validationErrors.corporate_number && (
                      <ErrorText>{this.state.validationErrors.corporate_number}</ErrorText>
                    )}
                  </InputWithErrorWrapper>
                </Grid.Item>
              )}

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>組織コード</Label>
                  <Input
                    syncValue={this.state.req.code}
                    maxLength="32"
                    onChange={(e) => {
                      if (/^[a-zA-Z0-9-]+$/.test(e.target.value) || e.target.value === '') {
                        this.setReq('code', e.target.value);
                      }
                    }}
                    fullWidth
                  />
                  {this.state.codePrefix && (
                    <span style={{ fontSize: '0.8em', color: '#666' }}>
                      空欄にすると「{this.state.codePrefix}-XXXXX」形式で自動採番されます
                    </span>
                  )}
                  {this.state.validationErrors.code && (
                    <ErrorText>{this.state.validationErrors.code}</ErrorText>
                  )}
                  <Alert color="danger" isOpen={this.isInvalidParameter('code')}>
                    組織コードは半角のアルファベット、数字、-
                    （ハイフン）から成る32文字以下の文字列である必要があります
                  </Alert>
                  <Alert color="danger" isOpen={this.isOrganizationConflict()}>
                    この組織コードは既に登録されています
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>
            </Grid.Item>

            <Grid.Item startColumn={3} size={6} flexDirection="column">
              <InputWithErrorWrapper>
                <Label>
                  組織名 <Required />
                </Label>
                <TextArea
                  maxLength="256"
                  onChange={(e) => this.setReq('name', e.target.value)}
                  fullWidth
                  rows={1}
                />
                {this.state.validationErrors.name && (
                  <ErrorText>{this.state.validationErrors.name}</ErrorText>
                )}
              </InputWithErrorWrapper>
            </Grid.Item>

            <Grid.Item startColumn={3} size={4} flexDirection="column">
              <Label>担当者名</Label>
              <Input onChange={(e) => this.setReq('contact_name', e.target.value)} fullWidth />
            </Grid.Item>

            <Grid.Divider />

            {/* メールアドレス section */}
            <Grid.Item size={2}>メールアドレス</Grid.Item>

            <Grid.Item size={4} flexDirection="column">
              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>
                    発行体への通知用 <Required />
                  </Label>
                  <Input
                    syncValue={this.state.req.issuer_admin_user_email}
                    onChange={(e) => testEmailAllowedLetters(e.target.value) && this.setReq('issuer_admin_user_email', e.target.value)}
                    fullWidth
                  />
                  {this.state.validationErrors.issuer_admin_user_email && (
                    <ErrorText>{this.state.validationErrors.issuer_admin_user_email}</ErrorText>
                  )}
                  <Alert color="danger" isOpen={this.isInvalidParameter('issuer_admin_user_email')}>
                    不正なメールアドレスです
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>
                    決済加盟店登録への招待用
                    <Required />
                  </Label>
                  <Input
                    syncValue={this.state.req.member_admin_user_email}
                    onChange={(e) => testEmailAllowedLetters(e.target.value) && this.setReq('member_admin_user_email', e.target.value)}
                    fullWidth
                  />
                  {this.state.validationErrors.member_admin_user_email && (
                    <ErrorText>{this.state.validationErrors.member_admin_user_email}</ErrorText>
                  )}
                  <Alert color="danger" isOpen={this.isInvalidParameter('member_admin_user_email')}>
                    不正なメールアドレスです
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>
            </Grid.Item>

            <Grid.Divider />

            {/* 振り込み先情報 section */}
            <Grid.Item size={2}>振り込み先情報</Grid.Item>

            <Grid.Item size={4} flexDirection="column">
              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>金融機関名</Label>
                  <Input onChange={(e) => this.setReq('bank_name', e.target.value)} fullWidth />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_name')}>
                    金融機関名は64文字以下の文字列である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>金融機関コード</Label>
                  <Input
                    pattern="\d{4}"
                    title="金融機関コードは4桁の半角数字である必要があります"
                    onChange={(e) => this.setReq('bank_code', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_code')}>
                    金融機関コードは4桁の半角数字である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>支店名</Label>
                  <Input
                    onChange={(e) => this.setReq('bank_branch_name', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_branch_name')}>
                    金融機関店舗名は64文字以下の文字列である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>支店コード</Label>
                  <Input
                    pattern="\d{3}"
                    title="金融機関店舗コードは3桁の半角数字である必要があります"
                    onChange={(e) => this.setReq('bank_branch_code', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_branch_code')}>
                    金融機関店舗コードは3桁の半角数字である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <Label>口座種別</Label>
                <RadioGroupWrapper>
                  <RadioWrapper>
                    <Radio
                      id="saving"
                      name="account-type"
                      type="radio"
                      value="saving"
                      checked={this.state.req.bank_account_type === 'saving'}
                      onChange={(e) => this.setReq('bank_account_type', e.target.value)}
                    />
                    <Label htmlFor="saving">普通</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="current"
                      name="account-type"
                      type="radio"
                      value="current"
                      checked={this.state.req.bank_account_type === 'current'}
                      onChange={(e) => this.setReq('bank_account_type', e.target.value)}
                    />
                    <Label htmlFor="current">当座</Label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      id="other"
                      name="account-type"
                      type="radio"
                      value="other"
                      checked={this.state.req.bank_account_type === 'other'}
                      onChange={(e) => this.setReq('bank_account_type', e.target.value)}
                    />
                    <Label htmlFor="other">その他</Label>
                  </RadioWrapper>
                </RadioGroupWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>口座番号</Label>
                  <Input
                    pattern="\d{0,7}"
                    title="金融機関口座番号は7桁以下の半角数字である必要があります"
                    onChange={(e) => this.setReq('bank_account', e.target.value)}
                    fullWidth
                  />
                  <Alert color="danger" isOpen={this.isInvalidParameter('bank_account')}>
                    金融機関口座番号は7桁以下の半角数字である必要があります
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>

              <Grid.Item flexDirection="column">
                <InputWithErrorWrapper>
                  <Label>口座名義人</Label>
                  <Input
                    pattern="^[0-9A-Zァ-ヶ　ｦ-ﾟ '＼￥，．ー「」（）／\\¥,.\-｢｣()/]+$"
                    title="金融機関口座名義人は30桁以下の半角数字、半角英字大文字、カナ文字、記号(¥,.-｢｣()/)である必要があります。また、全角は半角に変換されます。"
                    syncValue={this.state.req.bank_account_holder_name}
                    onChange={(e) => this.setReq('bank_account_holder_name', e.target.value)}
                    onBlur={(e) =>
                      this.setReq(
                        'bank_account_holder_name',
                        toJapaneseBankingSystemHalfWidth(toKatakana(e.target.value))
                      )
                    }
                    placeholder="例)ﾔﾏﾀﾞﾀﾛｳ"
                    fullWidth
                  />
                  <Alert
                    color="danger"
                    isOpen={this.isInvalidParameter('bank_account_holder_name')}
                  >
                    金融機関口座名義人は30桁以下の半角数字、半角英字大文字、カナ文字、記号(¥,.-｢｣()/)である必要があります。また、全角は半角に変換されます。
                  </Alert>
                </InputWithErrorWrapper>
              </Grid.Item>
            </Grid.Item>

            <Grid.Divider />

            {/* 各種費用 section */}
            <Grid.Item size={2}>各種費用</Grid.Item>

            <Grid.Item size={6} flexDirection="column">
              {this.state.selectedMoneyOptions.length === 0 ? (
                <div>加盟するマネー選択後、設定欄を表示します</div>
              ) : (
                this.state.selectedMoneyOptions.map((money) => (
                  <OrgMoneyFeeWrapper key={money.value.id}>
                    <Label style={{ fontWeight: 700 }}>{money.label}</Label>
                    <OrgMoneyFeeInputRowWrapper>
                      <OrgMoneyFeeInputWrapper>
                        <Label>決済手数料(％)</Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          syncValue={
                            this.state.moneyFees[money.value.id]?.payment_fee_rate_percentage || ''
                          }
                          onChange={(e) =>
                            this.setMoneyFee(
                              money.value.id,
                              'payment_fee_rate_percentage',
                              e.target.value
                            )
                          }
                          onBlur={(e) => {
                            const value = e.target.value;
                            if (value.endsWith('.')) {
                              this.setMoneyFee(
                                money.value.id,
                                'payment_fee_rate_percentage',
                                value.slice(0, -1)
                              );
                            }
                          }}
                          fullWidth
                        />
                      </OrgMoneyFeeInputWrapper>
                      <OrgMoneyFeeInputWrapper>
                        <Label>月額システム利用料</Label>
                        <LocalizedMask
                          value={this.state.moneyFees[money.value.id]?.system_fee || ''}
                          onChange={(e) =>
                            this.setMoneyFee(money.value.id, 'system_fee', e.target.value)
                          }
                        >
                          {({ value, ...inputProps }) => (
                            <Input {...inputProps} syncValue={value} fullWidth />
                          )}
                        </LocalizedMask>
                      </OrgMoneyFeeInputWrapper>
                      <OrgMoneyFeeInputWrapper>
                        <Label>その他固定費</Label>
                        <LocalizedMask
                          value={this.state.moneyFees[money.value.id]?.fixed_fee || ''}
                          onChange={(e) =>
                            this.setMoneyFee(money.value.id, 'fixed_fee', e.target.value)
                          }
                        >
                          {({ value, ...inputProps }) => (
                            <Input {...inputProps} syncValue={value} fullWidth />
                          )}
                        </LocalizedMask>
                      </OrgMoneyFeeInputWrapper>
                    </OrgMoneyFeeInputRowWrapper>
                  </OrgMoneyFeeWrapper>
                ))
              )}
            </Grid.Item>

            <Grid.Divider />

            <Grid.Item size={12}>
              <Button type="submit">登録</Button>
            </Grid.Item>
          </FormGrid>

          <Alert color="success" isOpen={this.isSuccess()}>
            決済加盟店を新規に作成し、管理ユーザ招待メールの送信に成功しました
          </Alert>
          <Alert color="danger" isOpen={this.isSendEmailError()}>
            指定されたメールアドレスへの招待メールの送信に失敗しました。決済加盟店は作成されません。
          </Alert>
          <Alert color="danger" isOpen={this.isOtherError()}>
            予期しないエラーが発生しました。決済加盟店は作成されません。
          </Alert>
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      showLoadingSpinner,
      hideLoadingSpinner,
    },
    dispatch
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CSSModules(CreateMemberOrganization, styles));
